import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { useDictation } from '../../../lib/use-dictation';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { apiFetch } from '../../../lib/api-client';
import { API_BASE } from '../../../lib/api-base';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { ComposerPill } from '../ComposerPill';
import { StagedFileChips } from '../HomeComposer/StagedFileChips';
import { useStagedFiles } from '../HomeComposer/use-staged-files';
import { Typography } from '../ui';
import styles from './outreach-thread.module.css';

/**
 * Answering an inbound message — the real composer, and an honest account of what pressing send does.
 *
 * **Why this is a hand-off and not a send.** A reply never reaches the other person directly. The
 * outreach service stores the words and then wakes the SENDER's agent with them
 * (`OutreachService.replyToOutreach` → `triggerReplyLoop`), and that agent decides what the recipient
 * actually sees. So the truthful shape is: the user writes, and their own agent relays it — which is
 * exactly what this does. The message goes into a real conversation carrying `references.outreachId`,
 * and the agent calls `ManageOutreaches` from there.
 *
 * **Why it reuses `ComposerPill` rather than growing its own field.** The one composer in the product
 * already does attachments, dictation and paste — and the attachment path is not decoration here: the
 * outreach service will only accept a file on a **permanent Wix-media URL** (its host allowlist is an
 * SSRF guard and an expiry guard), which is what the chat's upload produces and what the agent's
 * `resolveOutreachAttachments` re-hosts against. A second picker in this dialog would have to rebuild
 * all of it. `HomeComposer` and `ChannelLockedBar` already mount this pill outside the chat, so this is
 * the established shape rather than a new one.
 *
 * The conversation is resolved server-side first (`outreach-reply-conversation`): the outreach's own
 * thread when the user can SEND in it, a freshly minted one when they cannot. "Can send" and not "can
 * open": a WhatsApp/SMS outreach's thread is in the chat list yet read-only on every client, and an
 * EMAIL outreach's is not in the list at all. Resolving to either one closed this dialog, dropped the
 * draft and sent nothing. See `outreachReplyConversation.ts` for the rule.
 */
export function OutreachReplyComposer({ outreachId, agentName, onHandedOff }: {
  outreachId: string;
  agentName: string;
  /** Fired once the hand-off is on its way, so the dialog can close itself. */
  onHandedOff: () => void;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { requestUpload } = useActiveConversation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Dictation appends to the draft for review; it never auto-sends.
  const dictation = useDictation((spoken) => {
    const trimmed = spoken.trim();
    if (!trimmed) return;
    setText((prev) => (prev.trim() ? `${prev.trim()} ${trimmed}` : trimmed));
  });

  const { staged, stageFiles, removeAt, clear } = useStagedFiles(false);
  const canSend = (text.trim().length > 0 || staged.length > 0) && !busy;

  const submit = async () => {
    if (!canSend) return;
    logPageClickBi({ ...TASKS_PAGE, button_name: 'reply_to_outreach' });
    setBusy(true);
    setFailed(false);
    try {
      const res = await apiFetch(`${API_BASE}/api/outreach-reply-conversation`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outreachId }),
      });
      if (!res.ok) throw new Error(`resolve ${res.status}`);
      const { conversationId } = (await res.json()) as { conversationId?: string };
      if (!conversationId) throw new Error('no conversation');

      // The files ride the same hand-off: the chat uploads them and sends them WITH the text as one
      // message (see `use-autosend`), which is the only path that produces the permanent media URLs the
      // outreach service will accept.
      if (staged.length > 0) requestUpload(staged);
      const link = chatHandoffLink({ kind: 'existing', conversationId, message: text.trim(), outreachId }, basePath);
      clear();
      onHandedOff();
      void router.push(link.route, link.path);
    } catch {
      // The draft and the files stay exactly where they are — nothing was sent, so nothing is thrown
      // away, and the user can press send again.
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <form
      className={styles.composer}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) stageFiles(Array.from(files));
          e.target.value = '';
        }}
      />
      <StagedFileChips files={staged} onRemove={removeAt} />
      <ComposerPill
        value={text}
        onChange={setText}
        onSubmit={() => void submit()}
        placeholder={t('tasks.outreach.reply.placeholder', { agent: agentName })}
        onAttach={() => fileInputRef.current?.click()}
        attachLabel={t('chat.attachFile')}
        dictation={dictation}
        dictateLabel={t('chat.dictate')}
        transcribingLabel={t('chat.dictate.transcribing')}
        cancelLabel={t('common.cancel')}
        canSend={canSend}
        sendLabel={t('common.send')}
      />
      {/* Says what send actually does, before it is pressed. The alternative — a button that reads
          "Send" and quietly hands the words to an agent — is the thing this whole change exists to
          stop doing. */}
      <Typography variant="p3" component="p" color="secondary" className={styles.handoffNote}>
        {t('tasks.outreach.reply.handoffNote', { agent: agentName })}
      </Typography>
      {failed ? (
        <p className={styles.failed} role="alert">
          {t('tasks.outreach.reply.failed')}
        </p>
      ) : null}
    </form>
  );
}
